import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  AccessTime,
  Person,
  Camera,
  MarkEmailRead as MarkReadIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import type { NotificationData } from '../components/NotificationBell';
import NotificationDetailModal from '../components/NotificationDetailModal';
import { notificationAPI, type NotificationResponse } from '../utils/api';
import { buildNotificationImageUrl } from '../utils/notificationImageUrl';
import { useAuth } from '../hooks/useAuth';
import { formatDateTimeInNurseryTimezone } from '../utils/timezone';

// Helper function to convert API response to component format (same as NotificationBell)
const convertAPINotificationToComponentFormat = (notification: NotificationResponse, nurseryId: string, timezone: string): NotificationData => {
  const imageUrl = buildNotificationImageUrl(nurseryId, notification.image_id, notification.camera_number);
  
  return {
    id: notification.id,
    childName: notification.child_name,
    childId: notification.child_id,
    classroom: notification.classroom_name,
    camera: notification.camera_name,
    imageUrl: imageUrl,
    policyName: notification.policy_name,
    alertLevel: notification.alert_level,
    isRead: notification.read_flag === 1,
    similarity: notification.similarity ? notification.similarity * 100 : undefined,
    detectionTime: formatDateTimeInNurseryTimezone(notification.log_time, timezone || 'UTC'),
    title: notification.title,
    message: notification.message,
    actionType: notification.action_type,
    lastSeenAt: notification.last_seen_at,
    minutesAbsent: notification.minutes_absent,
    createdAt: notification.created_at,
    coordinates: notification.coordinates,
  };
};

const Notifications: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'notifications' });
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<NotificationData | null>(null);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showUnreadOnly, setShowUnreadOnly] = useState<boolean>(true); // Default to unread-only (avoids slow full-scan query and timeout)
  const { nursery_id } = useParams<{ nursery_id: string }>();
  const { user, currentNursery } = useAuth();

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(30);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Get nursery ID from route params or currentNursery
  const nurseryId = nursery_id || currentNursery?.nursery_id;

  // When nursery changes, clear list and reset filters/pagination
  useEffect(() => {
    if (!nurseryId) return;
    setNotifications([]);
    setError(null);
    setCurrentPage(1);
    setShowUnreadOnly(true);
    setPerPage(30);
  }, [nurseryId]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Fetch notifications with pagination
  const fetchNotifications = useCallback(async (page: number = 1, unreadOnly: boolean = false, itemsPerPage: number = 30) => {
    if (!user?.id || !nurseryId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await notificationAPI.getNotifications(nurseryId, user.id, {
        unread_only: unreadOnly,
        page: page,
        per_page: itemsPerPage
      });
      if (response.success) {
        const convertedNotifications = response.data.notifications.map(n => 
          convertAPINotificationToComponentFormat(n, nurseryId, currentNursery?.timezone || 'UTC')
        );
        setNotifications(convertedNotifications);
        
        // Update pagination state from response.data.pagination
        const pagination = response.data.pagination;
        if (pagination) {
          setCurrentPage(pagination.current_page);
          setTotalPages(pagination.last_page);
          setTotalCount(pagination.total);
          setPerPage(pagination.per_page);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch notifications:', err);
      
      // Show timeout-specific error message
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError(t('timeoutError'));
      } else {
        setError(t('loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id, nurseryId]);

  // Fetch notifications on mount and when filter/pagination changes
  useEffect(() => {
    fetchNotifications(currentPage, showUnreadOnly, perPage);
  }, [fetchNotifications, currentPage, showUnreadOnly, perPage]);

  // Reset to page 1 when filter changes
  const handleFilterChange = () => {
    setCurrentPage(1);
    setShowUnreadOnly(!showUnreadOnly);
  };

  // Handle page change
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle per page change
  const handlePerPageChange = (event: any) => {
    setPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page
  };

  const getAlertLevelColor = (level: string | null) => {
    switch (level) {
      case 'red':
        return 'error';
      case 'yellow':
        return 'warning';
      case 'green':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleNotificationClick = async (notification: NotificationData) => {
    setSelectedNotification(notification);
    setNotificationModalOpen(true);
    
    // Mark as read locally (optimistic update)
    setNotifications(prev =>
      prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
    );
    
    // Mark as read on server
    try {
      await notificationAPI.markAsRead(notification.id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      // Revert optimistic update on error
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, isRead: notification.isRead } : n)
      );
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id || !nurseryId) return;
    
    // Store original state for rollback
    const originalNotifications = [...notifications];
    
    // Mark all as read locally (optimistic update)
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    
    // Mark all as read on server
    try {
      const response = await notificationAPI.markAllAsRead(nurseryId, user.id);
      console.log(`Marked ${response.data.updated_count} notifications as read`);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      // Revert optimistic update on error
      setNotifications(originalNotifications);
      setError(t('markAllFailed'));
    }
  };


  return (
    <Layout title={t('title')}>
      <Box sx={{ width: '100%', px: 3, py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <NotificationsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {t('title')}
            </Typography>
            {!loading && unreadCount > 0 && (
              <Chip
                label={t('unread', { count: unreadCount })}
                color="error"
                size="small"
              />
            )}
            {!loading && totalCount > 0 && (
              <Chip
                label={t('total', { count: totalCount })}
                color="default"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            {!loading && unreadCount > 0 && (
              <Button
                variant="outlined"
                startIcon={<MarkReadIcon />}
                onClick={handleMarkAllAsRead}
              >
                {t('markAllAsRead')}
              </Button>
            )}
            {!loading && (
              <Button
                variant={showUnreadOnly ? 'contained' : 'outlined'}
                onClick={handleFilterChange}
              >
                {showUnreadOnly ? t('showAll') : t('showUnreadOnly')}
              </Button>
            )}
            {!loading && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>{t('perPage')}</InputLabel>
                <Select
                  value={perPage}
                  label={t('perPage')}
                  onChange={handlePerPageChange}
                >
                  <MenuItem value={15}>15</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Notifications Grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
              {notifications.map((notification) => (
            <Box key={notification.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  borderWidth: notification.isRead ? 1 : 2,
                  borderStyle: 'solid',
                  borderColor: notification.isRead ? 'divider' : 'primary.main',
                  backgroundColor: notification.isRead ? 'background.paper' : 'rgba(103, 126, 234, 0.05)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  },
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                <CardContent sx={{ p: 2.5 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: notification.alertLevel === 'red' ? 'error.main' : 
                                          notification.alertLevel === 'yellow' ? 'warning.main' : 
                                          notification.alertLevel === 'green' ? 'success.main' : 'grey.500',
                        }}
                      />
                      <Chip
                        label={notification.alertLevel?.toUpperCase() ?? 'INFO'}
                        color={getAlertLevelColor(notification.alertLevel) as any}
                        size="small"
                      />
                    </Box>
                    {!notification.isRead && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                        }}
                      />
                    )}
                  </Box>

                  {/* Policy Name */}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {notification.policyName}
                  </Typography>

                  {/* Detection Image Preview */}
                  <Box
                    sx={{
                      mb: 2,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: 1,
                      borderColor: 'divider',
                      backgroundColor: 'background.default',
                      height: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={notification.imageUrl}
                      alt={`Detection of ${notification.childName}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const errorMsg = document.createElement('div');
                        errorMsg.style.padding = '20px';
                        errorMsg.style.color = '#999';
                        errorMsg.style.textAlign = 'center';
                        errorMsg.textContent = t('imageNotAvailable');
                        target.parentElement?.appendChild(errorMsg);
                      }}
                    />
                  </Box>

                  {/* Child Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Person sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {notification.childName} ({notification.childId})
                    </Typography>
                  </Box>

                  {/* Camera Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Camera sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      {notification.camera}
                    </Typography>
                  </Box>

                  {/* Time */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AccessTime sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      {notification.detectionTime}
                    </Typography>
                  </Box>

                </CardContent>
              </Card>
            </Box>
          ))}
            </Box>

            {/* Pagination */}
            {notifications.length > 0 && totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, gap: 2 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
                {totalCount > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {t('showingRange', { from: ((currentPage - 1) * perPage) + 1, to: Math.min(currentPage * perPage, totalCount), total: totalCount })}
                  </Typography>
                )}
              </Box>
            )}

            {/* Empty State */}
            {notifications.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  {t('noNotifications')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('emptyHint')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Notification Detail Modal */}
      <NotificationDetailModal
        open={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
        notification={selectedNotification}
      />
    </Layout>
  );
};

export default Notifications;
